import React, { useEffect } from 'react';
import { Platform } from 'react-native';
import { Box, HStack, VStack } from '@gluestack-ui/themed';

import { DASHBOARD_EASE_OUT_CSS, DASHBOARD_MOTION } from './dashboardMotion';

const SHIMMER_LOW = '#F2F4F7';
const SHIMMER_HIGH = '#F9FAFB';
const SHIMMER_STYLE_ID = 'dashboard-shimmer-keyframes';

function ensureDashboardShimmerKeyframes() {
  if (Platform.OS !== 'web' || typeof document === 'undefined') {
    return;
  }
  if (document.getElementById(SHIMMER_STYLE_ID)) {
    return;
  }
  const style = document.createElement('style');
  style.id = SHIMMER_STYLE_ID;
  style.textContent = `
    @keyframes dashboardShimmerSweep {
      0% {
        background-position: 200% 0;
      }
      100% {
        background-position: -200% 0;
      }
    }
  `;
  document.head.appendChild(style);
}

function PlaceholderBlock({
  height,
  flex,
  width,
}: {
  height: number;
  flex?: number;
  width?: number | `${number}%`;
}) {
  return (
    <Box
      height={height}
      flex={flex}
      width={width}
      borderRadius="$md"
      bg={SHIMMER_LOW}
    />
  );
}

/**
 * Animated first-load placeholder (metrics pending, motion allowed).
 */
export function DashboardShimmer() {
  useEffect(() => {
    ensureDashboardShimmerKeyframes();
  }, []);

  return (
    <Box
      testID="dashboard-shimmer"
      mt="$4"
      borderRadius="$md"
      p="$4"
      sx={{
        _web: {
          background: `linear-gradient(90deg, ${SHIMMER_LOW} 0%, ${SHIMMER_HIGH} 50%, ${SHIMMER_LOW} 100%)`,
          backgroundSize: '200% 100%',
          animationName: 'dashboardShimmerSweep',
          animationDuration: `${DASHBOARD_MOTION.shimmerCycleMs}ms`,
          animationTimingFunction: DASHBOARD_EASE_OUT_CSS,
          animationIterationCount: 'infinite',
        },
      }}
    >
      <VStack space="md">
        <HStack space="md" alignItems="stretch">
          {[0, 1, 2, 3, 4].map((key) => (
            <Box key={key} flex={1}>
              <PlaceholderBlock height={12} width="60%" />
              <Box mt="$2">
                <PlaceholderBlock height={32} width="45%" />
              </Box>
              <Box mt="$2">
                <PlaceholderBlock height={40} />
              </Box>
            </Box>
          ))}
        </HStack>
        <PlaceholderBlock height={16} width="30%" />
        <PlaceholderBlock height={220} />
        <PlaceholderBlock height={16} width="35%" />
        <PlaceholderBlock height={240} />
      </VStack>
    </Box>
  );
}

/**
 * Static first-load placeholder when reduced motion is on.
 */
export function DashboardSkeleton() {
  return (
    <Box testID="dashboard-skeleton" mt="$4">
      <VStack space="md">
        <HStack space="md" alignItems="stretch">
          {[0, 1, 2, 3, 4].map((key) => (
            <Box key={key} flex={1}>
              <PlaceholderBlock height={12} width="60%" />
              <Box mt="$2">
                <PlaceholderBlock height={32} width="45%" />
              </Box>
              <Box mt="$2">
                <PlaceholderBlock height={40} />
              </Box>
            </Box>
          ))}
        </HStack>
        <PlaceholderBlock height={16} width="30%" />
        <PlaceholderBlock height={220} />
        <PlaceholderBlock height={16} width="35%" />
        <PlaceholderBlock height={240} />
      </VStack>
    </Box>
  );
}
